import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import Ipucu from './Ipucu';

/**
 * CARİ KARTINDAKİ ÇEK / SENET KUTUSU — carinin çek ve senetleri, risk toplamı.
 * Yalnız `cheque.view` yetkisinde çizilir (çağıran denetler). Gayriresmî çekler, onları
 * göremeyen role sunucudan hiç gelmez.
 */
type Satir = { id: number; tur: 'CEK' | 'SENET'; no: string; bankaAdi: string | null; vadeTarihi: string; tutarKurus: number; para: string; durum: string; durumAd: string; bekliyor: boolean; kalanGun: number };
type Risk = { para: string; bekleyenKurus: number; karsiliksizKurus: number; karsiliksizAdet: number; riskKurus: number };
type Veri = { satirlar: Satir[]; toplam: number; risk: Risk[] };

const para = (kurus: number, birim: string) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: birim, minimumFractionDigits: kurus % 100 === 0 ? 0 : 2, maximumFractionDigits: kurus % 100 === 0 ? 0 : 2,
}).format(kurus / 100);
const gun = (g: string) => `${g.slice(8, 10)}.${g.slice(5, 7)}.${g.slice(0, 4)}`;
const ROZET: Record<string, string> = { PORTFOY: 'ccd-badge-info', BANKADA: 'ccd-badge-accent', TEMINATTA: 'ccd-badge-warning', TAHSIL: 'ccd-badge-success', KARSILIKSIZ: 'ccd-badge-danger' };

export default function CariCek({ cariId }: { cariId: number }) {
  const [v, setV] = useState<Veri | null>(null);
  useEffect(() => { setV(null); api<Veri>(`/api/cek/cari/${cariId}`).then(setV).catch(() => undefined); }, [cariId]);
  // Hiç çeki / senedi olmayan caride kutu yer kaplamaz.
  if (!v || v.toplam === 0) return null;
  return (
    <div className="ccd-card ccd-stack-sm">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="ccd-fieldset-title">Çek ve senetleri ({v.toplam})</div>
        <Link className="ccd-btn ccd-btn-ghost ccd-btn-sm" to="/cek-senet">Çek / Senet →</Link>
      </div>
      <div className="ccd-kasa-cari-ozet">
        {v.risk.length === 0
          ? <span className="ccd-meta">Açık çek riski yok <Ipucu id="cek-riski" /></span>
          : v.risk.map((r) => (
            <span key={r.para} className={`ccd-kasa-yon ${r.karsiliksizKurus > 0 ? 'ccd-kasa-yon-alacak' : 'ccd-kasa-yon-bizde'}`} data-fihrist="cek-riski">
              Çek riski: <b>{para(r.riskKurus, r.para)}</b>{r.karsiliksizKurus > 0 && <> · karşılıksız {para(r.karsiliksizKurus, r.para)}</>}
            </span>
          ))}
        {v.risk.length > 0 && <Ipucu id="cek-riski" />}
      </div>
      <div className="ccd-table-wrap">
        <table className="ccd-table">
          <thead><tr><th className="ccd-th">Vade</th><th className="ccd-th">No / banka</th><th className="ccd-th ccd-td-num">Tutar</th><th className="ccd-th">Durum</th></tr></thead>
          <tbody>
            {v.satirlar.map((c) => (
              <tr key={c.id} className="ccd-tr">
                <td className="ccd-td ccd-td-nowrap">{gun(c.vadeTarihi)}{c.bekliyor && c.kalanGun < 0 && <div className="ccd-meta ccd-kasa-cikis">{-c.kalanGun} gün geçti</div>}</td>
                <td className="ccd-td"><Link to={`/cek-senet?cek=${c.id}`}>{c.tur === 'CEK' ? 'Çek' : 'Senet'} {c.no}</Link>{c.bankaAdi && <div className="ccd-meta">{c.bankaAdi}</div>}</td>
                <td className="ccd-td ccd-td-num">{para(c.tutarKurus, c.para)}</td>
                <td className="ccd-td"><span className={`ccd-badge ${ROZET[c.durum] ?? ''}`}>{c.durumAd}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {v.toplam > v.satirlar.length && <div className="ccd-meta">Son {v.satirlar.length} kayıt gösteriliyor; tamamı Çek / Senet ekranında.</div>}
    </div>
  );
}
